import type { BildInfo } from "@gui-creator/gemeinsam";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { api } from "../api/klient.ts";

const ID = /^[a-z0-9_.-]+:[a-z0-9_./-]+$/;
const datei = (pfad: string) => `/api/texturen/datei/${pfad}`;

export function useBildInfo(symbol: string) {
  return useQuery({
    queryKey: ["bild", symbol],
    queryFn: () => api<BildInfo>(`/api/items/${encodeURIComponent(symbol)}/bild`),
    enabled: ID.test(symbol),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

function ladeBild(src: string): Promise<HTMLImageElement> {
  return new Promise((ok, fehler) => {
    const b = new Image();
    b.onload = () => ok(b);
    b.onerror = fehler;
    b.src = src;
  });
}

/** Würfel in der Spielansicht: oben voll hell, links 80 %, rechts 60 %. */
function Wuerfel({ info, groesse }: { info: Extract<BildInfo, { art: "wuerfel" }>; groesse: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let aktiv = true;
    void Promise.all([ladeBild(datei(info.oben)), ladeBild(datei(info.seite)), ladeBild(datei(info.front ?? info.seite))]).then(([oben, links, rechts]) => {
      const c = ref.current;
      if (!aktiv || !c) return;
      const dpr = window.devicePixelRatio || 1;
      const s = groesse * dpr;
      c.width = s;
      c.height = s;
      const ctx = c.getContext("2d")!;
      ctx.imageSmoothingEnabled = false;
      const seite = (bild: HTMLImageElement, a: number, b: number, cc: number, d: number, e: number, f: number, schatten: number) => {
        const w = bild.width;
        const h = Math.min(bild.height, w);
        ctx.setTransform(a / w, b / w, cc / h, d / h, e, f);
        ctx.drawImage(bild, 0, 0, w, h, 0, 0, w, h);
        if (schatten > 0) {
          ctx.fillStyle = `rgb(0 0 0 / ${schatten})`;
          ctx.fillRect(0, 0, w, h);
        }
      };
      const m = s * 0.06;
      const k = s - 2 * m;
      seite(oben, k / 2, -k / 4, k / 2, k / 4, m, m + k / 4, 0);
      seite(links, k / 2, k / 4, 0, k / 2, m, m + k / 4, 0.2);
      seite(rechts, k / 2, -k / 4, 0, k / 2, m + k / 2, m + k / 2, 0.4);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }, () => {});
    return () => {
      aktiv = false;
    };
  }, [info, groesse]);
  return <canvas ref={ref} style={{ width: groesse, height: groesse, display: "block" }} aria-hidden="true" />;
}

function farbtonVon(text: string): number {
  let h = 0;
  for (const z of text) h = (h * 31 + z.charCodeAt(0)) % 360;
  return h;
}

function Ersatz({ text, groesse }: { text: string; groesse: number }) {
  const kurz = text.startsWith("{") ? "{…}" : text.replace(/^.*\//, "").split("_").map((t) => t[0] ?? "").join("").slice(0, 3) || "?";
  const h = farbtonVon(text);
  return (
    <span
      className="item-ersatz"
      aria-hidden="true"
      style={{ width: groesse, height: groesse, fontSize: Math.max(8, groesse * 0.34), background: `hsl(${h} 32% 86%)`, color: `hsl(${h} 40% 28%)` }}
    >
      {kurz.toUpperCase()}
    </span>
  );
}

export function ItemBild({ symbol, groesse }: { symbol: string; groesse: number }) {
  const info = useBildInfo(symbol);
  const pfad = symbol.replace(/^minecraft:/, "");
  if (!ID.test(symbol)) return <Ersatz text={symbol} groesse={groesse} />;
  if (!info.data) return <span style={{ width: groesse, height: groesse, display: "block" }} />;
  if (info.data.art === "flach") {
    return <img src={datei(info.data.textur)} width={groesse} height={groesse} alt="" draggable={false} style={{ imageRendering: "pixelated", display: "block" }} />;
  }
  if (info.data.art === "wuerfel") return <Wuerfel info={info.data} groesse={groesse} />;
  return <Ersatz text={pfad} groesse={groesse} />;
}
